/**
 * Notifications Bell Component
 * Shows new features/updates notifications to users
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Sparkles, X, ChevronRight, Check } from 'lucide-react';
import axios from 'axios';

import { API } from '../config/api';

// Storage keys
const LAST_SEEN_VERSION_KEY = 'nishikigoi_last_seen_version';
const DISMISSED_NOTIFS_KEY = 'nishikigoi_dismissed_notifications';

export const NotificationsBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/changelog/`);
      const allVersions = response.data;
      
      // Get last seen version and dismissed notifications
      const lastSeenVersion = localStorage.getItem(LAST_SEEN_VERSION_KEY);
      const dismissedStr = localStorage.getItem(DISMISSED_NOTIFS_KEY);
      const dismissed = dismissedStr ? JSON.parse(dismissedStr) : [];
      
      // Filter to show only recent versions (last 5) and mark as new/read
      const recentVersions = allVersions.slice(0, 5).map(version => ({
        ...version,
        isNew: !lastSeenVersion || compareVersions(version.version, lastSeenVersion) > 0,
        isDismissed: dismissed.includes(version.version)
      }));
      
      setNotifications(recentVersions);
      
      // Count unread (new and not dismissed)
      const unread = recentVersions.filter(n => n.isNew && !n.isDismissed).length;
      setUnreadCount(unread);
      
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Compare version strings (V1.5.0 > V1.4.0)
  const compareVersions = (v1, v2) => {
    const parse = (v) => v.replace('V', '').split('.').map(Number);
    const [a1, a2, a3] = parse(v1);
    const [b1, b2, b3] = parse(v2);
    
    if (a1 !== b1) return a1 - b1;
    if (a2 !== b2) return a2 - b2;
    return (a3 || 0) - (b3 || 0);
  };

  const markAllAsRead = () => {
    if (notifications.length > 0) {
      // Save the latest version as seen
      localStorage.setItem(LAST_SEEN_VERSION_KEY, notifications[0].version);
      
      // Clear unread count
      setUnreadCount(0);
      
      // Update notifications state
      setNotifications(prev => prev.map(n => ({ ...n, isNew: false })));
    }
  };

  const dismissNotification = (version) => {
    const dismissedStr = localStorage.getItem(DISMISSED_NOTIFS_KEY);
    const dismissed = dismissedStr ? JSON.parse(dismissedStr) : [];
    
    if (!dismissed.includes(version)) {
      dismissed.push(version);
      localStorage.setItem(DISMISSED_NOTIFS_KEY, JSON.stringify(dismissed));
    }
    
    setNotifications(prev => prev.map(n => 
      n.version === version ? { ...n, isDismissed: true } : n
    ));
    
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleOpenDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      // Mark as read after a short delay when opening
      setTimeout(markAllAsRead, 2000);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleOpenDropdown}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Notifications"
        data-testid="notifications-bell"
      >
        <Bell className="w-5 h-5 text-[#0F172A] dark:text-white" />
        
        {/* Unread Badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D32F2F] text-white text-xs font-bold rounded-full flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden z-50"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between bg-gradient-to-r from-[#D32F2F]/5 to-transparent">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#D32F2F]" />
                <h3 className="font-semibold text-[#0F172A]">Nouveautés</h3>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-[#D32F2F] hover:underline"
                >
                  Tout marquer lu
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-8 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-[#D32F2F] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-[#0F172A]/50">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucune notification</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {notifications.map((notif) => (
                    <div
                      key={notif.version}
                      className={`p-4 hover:bg-stone-50 transition-colors ${
                        notif.isNew && !notif.isDismissed ? 'bg-[#D32F2F]/5' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          notif.type === 'feature' ? 'bg-emerald-100 text-emerald-600' :
                          notif.type === 'fix' ? 'bg-orange-100 text-orange-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          <Sparkles className="w-4 h-4" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-xs font-bold text-[#D32F2F]">
                              {notif.version}
                            </span>
                            {notif.isNew && !notif.isDismissed && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-[#D32F2F] text-white rounded-full">
                                NOUVEAU
                              </span>
                            )}
                          </div>
                          
                          <p className="font-medium text-sm text-[#0F172A] mb-1">
                            {notif.title}
                          </p>
                          
                          <p className="text-xs text-[#0F172A]/50">
                            {formatDate(notif.date)}
                          </p>
                          
                          {/* Changes preview */}
                          {notif.changes && notif.changes.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {notif.changes.slice(0, 2).map((change, i) => (
                                <li key={i} className="text-xs text-[#0F172A]/60 flex items-start gap-1.5">
                                  <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" />
                                  <span className="line-clamp-1">{change}</span>
                                </li>
                              ))}
                              {notif.changes.length > 2 && (
                                <li className="text-xs text-[#0F172A]/40 pl-4">
                                  +{notif.changes.length - 2} autres
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                        
                        {/* Dismiss button */}
                        {notif.isNew && !notif.isDismissed && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissNotification(notif.version);
                            }}
                            className="p-1 hover:bg-stone-200 rounded-full transition-colors"
                            title="Marquer comme lu"
                          >
                            <Check className="w-4 h-4 text-[#0F172A]/40" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-stone-100 bg-stone-50">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsOpen(false);
                  // Trigger changelog modal in footer
                  const versionBtn = document.querySelector('button[title="Voir l\'historique des versions"]');
                  if (versionBtn) versionBtn.click();
                }}
                className="text-xs text-[#D32F2F] hover:underline flex items-center gap-1"
              >
                Voir tout l'historique
                <ChevronRight className="w-3 h-3" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationsBell;
